import React, { Suspense } from "react";
import {
  NoDataDisplay,
  BackButton,
  LoadingState,
  StatusModals,
  ReceivingDiscrepancyHeadingSection,
  IssueDetailsSection,
  ReceivingDiscrepancyProductsSection,
  NonOrderProductCheckInsSection,
  ReceivingDiscrepancyDefectsSection,
  ModelFooterSection
} from "src/components";
import { ReceivingDiscrepancy } from "src/types";

export interface ReceivingDiscrepancyDetailLayoutProps {
  purchaseOrderId: string;
  receivingDiscrepancy: ReceivingDiscrepancy | null | undefined;
  isLoading: boolean;
  isRefetching: boolean;
  fetchError: unknown;
  refetch: () => void;
  contextSlot?: React.ReactNode;
  resolveSlot?: React.ReactNode;
}

export const ReceivingDiscrepancyDetailLayout: React.FC<ReceivingDiscrepancyDetailLayoutProps> = ({
  purchaseOrderId,
  receivingDiscrepancy,
  isLoading,
  isRefetching,
  fetchError,
  refetch,
  contextSlot,
  resolveSlot
}) => {
  if (isLoading) {
    return <LoadingState type="LOADING" />;
  }
  if (isRefetching) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return <LoadingState type="LOADING" />;
  }
  if (fetchError) {
    return <StatusModals.DataFetchError onRetry={refetch} />;
  }
  if (!receivingDiscrepancy) {
    return <NoDataDisplay />;
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto">
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <div className="bg-black px-8 py-6">
              <ReceivingDiscrepancyHeadingSection
                purchaseOrderId={purchaseOrderId}
                status={receivingDiscrepancy.status}
              />
            </div>

            <div className="p-8">
              <div className="space-y-6">
                <IssueDetailsSection
                  resolutionDetails={receivingDiscrepancy.resolutionDetails}
                  status={receivingDiscrepancy.status}
                  resolvedByEmail={receivingDiscrepancy.resolvedByEmail}
                />

                <ReceivingDiscrepancyProductsSection
                  orderProductDiscrepancies={receivingDiscrepancy.orderProductDiscrepancies}
                  fulfilmentRate={receivingDiscrepancy.fulfilmentRate}
                />

                <NonOrderProductCheckInsSection nonOrderProducts={receivingDiscrepancy.nonOrderProducts ?? []} />

                <ReceivingDiscrepancyDefectsSection defects={receivingDiscrepancy.defects ?? []} />

                {contextSlot}

                {resolveSlot}

                <div className="pt-6">
                  <BackButton />
                </div>

                <ModelFooterSection
                  createdAt={receivingDiscrepancy.createdAt}
                  updatedAt={receivingDiscrepancy.updatedAt}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
};
